import { Component, OnInit, OnDestroy, HostListener, EventEmitter, Output, inject, ChangeDetectionStrategy } from '@angular/core';

import { RouterModule } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { User, UserStats } from '../../../core/models/user.model';
import { UserService } from '../../../core/services/user.service';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { UserStatusDialogComponent } from '../user-status-dialog/user-status-dialog.component';
import { UserGroupsDialogComponent } from '../user-groups-dialog/user-groups-dialog.component';
import { HasPermissionDirective } from '../../../core/directives/has-permission.directive';
import { PermissionService } from '../../../core/services/permission.service';
import { UserRolesInlineDialogComponent } from '../user-roles-inline-dialog/user-roles-inline-dialog.component';
import { NotificationService } from '../../../core/services/notification.service';

@Component({
  selector: 'app-user-list',
  standalone: true,
  imports: [
    RouterModule,
    MatTableModule,
    MatPaginatorModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatChipsModule,
    MatTooltipModule,
    MatCardModule,
    MatSlideToggleModule,
    MatProgressBarModule,
    MatMenuModule,
    MatBadgeModule,
    ReactiveFormsModule,
    MatSnackBarModule,
    HasPermissionDirective
],
  templateUrl: './user-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./user-list.component.scss'],
})
export class UserListComponent implements OnInit, OnDestroy {

  allUsers: User[] = [];
  totalAllUsers = 0;
  users: User[] = [];
  totalUsers = 0;
  pageSize = 10;
  currentPage = 0;
  totalPages = 0;
  clientId = 'habilitation';
  loading = false;
  selectedFilter = 'all';
  activeDropdownId: string | null = null;

  stats: UserStats | null = null;
  statsLoading = false;

  // ── Permissions (accès direct au snapshot pour le template) ───────────────
  private permissions = inject(PermissionService);

  /** Expose le service au template pour les appels `can()` inline */
  get perm(): PermissionService { return this.permissions; }

  private userService = inject(UserService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);

  private usersBeingToggled = new Set<string>();
  searchControl = new FormControl('');
  private destroy$ = new Subject<void>();

  @Output() pageChanged = new EventEmitter<number>();

  private notificationService = inject(NotificationService);
  processingUserIds = new Set<string>();

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.setupSearch();
    this.loadUsersFromAPI();
    this.loadStats();
    // S'abonner aux données mises en cache
    this.userService.users$
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response) {
          this.users = response.content || [];
          this.totalUsers = response.totalElements || 0;
          this.totalPages = Math.ceil(this.totalUsers / this.pageSize);
          this.loading = false;
        } else {
          // Si response est null, on peut afficher un état de chargement
          // ou déclencher manuellement le chargement
          this.loadUsers();
        }
      });

    // Charger les données initiales (si le cache est vide)
    this.loadUsers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadUsers(): void {
    this.loading = true;
    const searchTerm = this.searchControl.value?.trim() || '';
    const status = this.selectedFilter;

    // Appel qui gère automatiquement le cache
    this.userService
      .getAllUsersV2(this.currentPage, this.pageSize, searchTerm, status)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Les données seront émises via le BehaviorSubject, donc pas besoin de les assigner ici
          // Mais on peut garder cette logique en fallback
          this.users = response.content || [];
          this.totalUsers = response.totalElements || 0;
          this.totalPages = Math.ceil(this.totalUsers / this.pageSize);
          this.loading = false;
        },
        error: (error) => {
          this.loading = false;
          // Gestion d'erreur...
        },
      });
  }

  toggleDropdown(userId?: string): void {
    this.activeDropdownId = this.activeDropdownId === userId ? null : (userId ?? null);
  }

  @HostListener('document:click', ['$event'])
  closeDropdowns(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.table-actions-icons')) {
      this.activeDropdownId = null;
    }
  }

  setupSearch(): void {
    this.searchControl.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.currentPage = 0;
      this.loadUsersFromAPI();
    });
  }

  private loadUsersFromAPI(): void {
    this.loading = true;
    const searchTerm = this.searchControl.value?.trim() || '';
    const status = this.selectedFilter;

    this.userService.getAllUsersV2(this.currentPage, this.pageSize, searchTerm, status)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.users = response.content || [];
          this.totalUsers = response.totalElements || 0;
          this.totalPages = Math.ceil(this.totalUsers / this.pageSize);
          this.loading = false;
        },
        error: (error) => {
          const messages: Record<number, string> = {
            403: 'Vous n\'avez pas les permissions nécessaires pour voir les utilisateurs',
            401: 'Session expirée. Veuillez vous reconnecter.',
            404: 'Endpoint introuvable. Vérifiez la configuration de l\'API.',
          };
          this.showError(messages[error.status] ?? 'Erreur lors du chargement des utilisateurs');
          this.users = [];
          this.totalUsers = this.totalAllUsers = this.totalPages = 0;
          this.loading = false;
        },
      });
  }

  private applyLocalFilters(): void {
    switch (this.selectedFilter) {
      case 'verified':
        this.users = this.allUsers.filter(u => u.emailVerified);
        break;
      case 'unverified':
        this.users = this.allUsers.filter(u => !u.emailVerified);
        break;
      default:
        this.users = [...this.allUsers];
    }
    this.totalUsers = this.users.length;
    this.totalPages = Math.ceil(this.totalAllUsers / this.pageSize);
  }

  onFilterSelect(event: Event): void {
    this.selectedFilter = (event.target as HTMLSelectElement).value;
    this.currentPage = 0;
    this.loadUsersFromAPI();
  }

  goToPage(page: number): void {
    this.currentPage = page;
    this.loadUsersFromAPI();
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0) this.goToPage(this.currentPage - 1);
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages - 1) this.goToPage(this.currentPage + 1);
  }

  getDisplayPages(): (number | string)[] {
    const pages: (number | string)[] = [];
    const total = this.totalPages;
    const current = this.currentPage + 1;

    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    pages.push(1);
    if (current > 3) pages.push('...');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
      pages.push(i);
    }
    if (current < total - 2) pages.push('...');
    pages.push(total);
    return pages;
  }

  openStatusDialog(user: User, event: Event): void {
    // Guard de permission côté composant (défense en profondeur)
    if (!this.permissions.can('users.toggle_status')) {
      (event.target as HTMLInputElement).checked = user.enabled;
      this.showError('Vous n\'avez pas les droits pour modifier le statut');
      return;
    }

    const checkbox = event.target as HTMLInputElement;
    const newStatus = checkbox.checked;
    checkbox.checked = user.enabled; // revert visuellement en attendant confirmation

    const dialogRef = this.dialog.open(UserStatusDialogComponent, {
      width: '400px',
      data: { user, newStatus },
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) this.executeToggleUserStatus(user, newStatus);
    });
  }

  private executeToggleUserStatus(user: User, newStatus: boolean): void {
    this.userService.toggleUserStatus(user.id, newStatus).subscribe({
      next: (updated) => {
        // Mise à jour locale
        const idx = this.users.findIndex((u) => u.id === updated.id);
        if (idx !== -1) this.users[idx] = { ...this.users[idx], ...updated };

        // ⚠️ Invalider le cache pour que les prochains appels rafraîchissent
        this.userService.invalidateCache();

        // Optionnel : recharger immédiatement pour être sûr (mais le cache est invalidé,
        // le prochain appel rechargera)
        this.loadUsers();
      },
      error: (error) => {
        user.enabled = newStatus;
        this.usersBeingToggled.delete(user.id);
        this.showErrorMessage(
          error.status === 403
            ? 'Vous n\'avez pas l\'autorisation de modifier cet utilisateur'
            : `Erreur lors de la modification du statut de ${user.username}`
        );
      },
    });
  }

  isUserBeingToggled(userId: string): boolean {
    return this.usersBeingToggled.has(userId);
  }

  openGroupsDialog(user: User): void {
    // Guard de permission côté composant
    if (!this.permissions.can('users.manage_groups')) {
      this.showError('Vous n\'avez pas les droits pour gérer les groupes');
      return;
    }
    this.activeDropdownId = null;
    if (!user?.id) { this.showError('Utilisateur non valide'); return; }

    this.dialog.open(UserGroupsDialogComponent, {
      width: '950px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: { user },
      panelClass: 'custom-dialog-container',
    }).afterClosed().subscribe(result => {
      this.activeDropdownId = null;
      if (result?.modified) this.loadUsersFromAPI();
    });
  }

  // ─── Gestion des rôles sous forme de pop-up ─────────────────
  openRolesDialog(user: User): void {

    if (!this.permissions.can('users.manage_roles')) {
      this.showError('Vous n\'avez pas les droits pour gérer les rôles');
      return;
    }

    if (!user?.id) { this.showError('Utilisateur non valide'); return; }

    this.dialog.open(UserRolesInlineDialogComponent, {
      data: { user },
      // No width/height here — the component controls its own size via CSS
      panelClass: 'roles-dialog-overlay',
      maxWidth: '95vw',
      maxHeight: '95vh',
      autoFocus: false,
    }).afterClosed().subscribe(result => {
      // Optionally reload if roles were modified
      if (result?.modified) this.loadUsersFromAPI();
    });
  }

  sendEmailVerification(user: User): void {
    if (this.processingUserIds.has(user.id) || user.emailVerified) return;
    this.processingUserIds.add(user.id);
    this.activeDropdownId = null;

    this.userService.sendVerificationEmail(user.id).subscribe({
      next: () => {
        this.processingUserIds.delete(user.id);
        this.notificationService.showSuccess({ title: 'Email envoyé', message: 'Un email de vérification a été envoyé.' });
      },
      error: (error) => {
        this.processingUserIds.delete(user.id);
        this.notificationService.showError({ title: 'Erreur', message: 'Échec de l\'envoi.' });
      }
    });
  }

  onResetPassword(user: User): void {
    if (this.processingUserIds.has(user.id)) return;
    this.processingUserIds.add(user.id);
    this.activeDropdownId = null; // close dropdown

    this.userService.resetPassword(user.id).subscribe({
      next: () => {
        this.processingUserIds.delete(user.id);
        this.notificationService.showSuccess({ title: 'Email envoyé', message: 'Un email de réinitialisation a été envoyé.' });
      },
      error: (error) => {
        this.processingUserIds.delete(user.id);
        const msg = error.status === 403 ? 'Vous n\'avez pas l\'autorisation.' : 'Erreur lors de l\'envoi.';
        this.notificationService.showError({ title: 'Erreur', message: msg });
      }
    });
  }

  // ── Snackbars ─────────────────────────────────────────────────────────────
  private showSuccessMessage(msg: string): void {
    this.snackBar.open(msg, 'Fermer', { duration: 3000, panelClass: ['success-snackbar'], horizontalPosition: 'end', verticalPosition: 'top' });
  }

  private showErrorMessage(msg: string): void {
    this.snackBar.open(msg, 'Fermer', { duration: 5000, panelClass: ['error-snackbar'], horizontalPosition: 'end', verticalPosition: 'top' });
  }

  private showInfo(msg: string): void {
    this.snackBar.open(msg, 'Fermer', { duration: 4000, panelClass: ['info-snackbar'], horizontalPosition: 'end', verticalPosition: 'top' });
  }

  private showError(msg: string): void {
    this.snackBar.open(msg, 'Fermer', { duration: 5000, panelClass: ['error-snackbar'], horizontalPosition: 'end', verticalPosition: 'top' });
  }

  /**
   * Télécharge le modèle Excel de création d'utilisateur
   */
  downloadTemplate(): void {
    const fileUrl = 'assets/templates/fiche_creation_utilisateur.xlsx';
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = 'fiche_creation_utilisateur.xlsx';
    link.click();
  }

  // user-list.component.ts
  loadStats(): void {
    this.statsLoading = true;
    this.userService.getStats()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (stats) => {
          // console.log('Stats OK:', stats);
          this.stats = stats;
          this.statsLoading = false;
        },
        error: (err) => {
          console.error('Stats error:', err);
          this.statsLoading = false;
          // On peut afficher un message mais ne pas bloquer l'affichage
          this.stats = { total: 0, verified: 0, unverified: 0, enabled: 0, disabled: 0 };
          this.showError('Impossible de charger les statistiques');
        }
      });
  }
}